import React, { useState, useEffect } from 'react';
import Drawer from '../common/Drawer';
import FormField from '../common/FormField';
import Button from '../common/Button';
import SignatureCapture from '../common/SignatureCapture';
import { UpdateDebitOrderMandateRequestDTO, MandateEditFormDataDTO } from '../../services/apiClient';
import { useDebitOrderMandateApi } from '../../hooks/useDebitOrderMandateApi';
import { useFileApi } from '../../hooks/useFileApi';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { showAlert } from '../../utils/sweetAlertUtils';
import { base64ToFile } from '../../utils/fileUtils';
import handleChange from '../../utils/formUtils/handleChange';
import { Save } from 'lucide-react';

interface EditMandateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mandateId: string;
  onSuccess?: () => void;
}

const validationRules: ValidationRules = {
  accountHolder: [{ required: true, message: 'Account holder name is required.' }],
  bankId: [{ required: true, message: 'Please select a bank.' }],
  accountNumber: [
    { required: true, message: 'Account number is required.' },
    { pattern: /^\d+$/, message: 'Account number must contain only digits.' },
  ],
  accountType: [{ required: true, message: 'Please select an account type.' }],
  signedAt: [{ required: true, message: 'Please enter the place where the mandate was signed.' }],
  amount: [{ required: true, message: 'Amount is required.' }],
};

const accountTypeOptions = [
  { value: 'current', label: 'Current' },
  { value: 'savings', label: 'Savings' },
  { value: 'transmission', label: 'Transmission' },
];

const EditMandateDrawer: React.FC<EditMandateDrawerProps> = ({
  isOpen,
  onClose,
  mandateId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<UpdateDebitOrderMandateRequestDTO>({} as UpdateDebitOrderMandateRequestDTO);
  const [editData, setEditData] = useState<MandateEditFormDataDTO | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { getMandateEditFormData, updateMandate, isLoading } = useDebitOrderMandateApi();
  const { uploadFile, isLoading: isUploadingSignature } = useFileApi();
  const { errors, validate } = useFormValidation<UpdateDebitOrderMandateRequestDTO>(validationRules);

  useEffect(() => {
    if (!isOpen || !mandateId) return;

    setSignatureData(null);

    const fetchData = async () => {
      const response = await getMandateEditFormData(mandateId);
      if (response?.data) {
        const data = response.data;
        setEditData(data);
        setFormData({
          accountHolder: data.accountHolder,
          bankId: data.bankId,
          accountNumber: data.accountNumber,
          accountType: data.accountType,
          signedAt: data.signedAt ?? undefined,
          amount: data.amount,
        });
      }
    };

    fetchData();
  }, [isOpen, mandateId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate(formData)) return;

    setSubmitting(true);
    try {
      let signatureFileId: string | undefined;

      if (signatureData) {
        const filename = `signature-${editData?.memberId}-${Date.now()}.webp`;
        const file = base64ToFile(signatureData, filename);
        const uploadResponse = await uploadFile('mandate-signatures', filename, file);

        if (!uploadResponse?.success || !uploadResponse.data) {
          await showAlert({
            title: 'Error',
            text: 'Failed to upload signature. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
          return;
        }
        signatureFileId = uploadResponse.data;
      }

      const requestData: UpdateDebitOrderMandateRequestDTO = {
        ...formData,
        ...(signatureFileId ? { signatureFileId } : {}),
      };

      const response = await updateMandate(mandateId, requestData);

      if (response?.data) {
        await showAlert({
          title: 'Mandate Updated',
          text: 'The mandate has been updated and queued for resubmission to Netcash.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      await showAlert({
        title: 'Error',
        text: 'Failed to update mandate. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const bankOptions = editData?.banks?.map((bank) => ({
    value: bank.id,
    label: bank.name || '',
  })) ?? [];

  const isBusy = isLoading || submitting || isUploadingSignature;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Edit Debit Order Mandate" size="small">
      {isLoading && !editData ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField
            id="amount"
            label="Debit Amount (ZAR)"
            type="number"
            value={formData.amount}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="0.00"
            error={errors.amount}
            required
            disabled={isBusy}
          />

          <FormField
            id="accountHolder"
            label="Account Holder Name"
            type="text"
            value={formData.accountHolder}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter account holder name"
            error={errors.accountHolder}
            required
            disabled={isBusy}
          />

          <FormField
            id="bankId"
            label="Bank"
            type="dropdown"
            value={formData.bankId}
            onChange={(e) => handleChange(e, setFormData)}
            options={bankOptions}
            placeholder="Select your bank"
            error={errors.bankId}
            required
            disabled={isBusy}
          />

          <FormField
            id="accountNumber"
            label="Account Number"
            type="text"
            value={formData.accountNumber}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter account number"
            error={errors.accountNumber}
            required
            disabled={isBusy}
          />

          <FormField
            id="accountType"
            label="Account Type"
            type="dropdown"
            value={formData.accountType}
            onChange={(e) => handleChange(e, setFormData)}
            options={accountTypeOptions}
            placeholder="Select your account type"
            error={errors.accountType}
            required
            disabled={isBusy}
          />

          <FormField
            id="signedAt"
            label="Signed At"
            type="text"
            value={formData.signedAt}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="City or town where mandate was signed"
            error={errors.signedAt}
            required
            disabled={isBusy}
            helper="City or town where the mandate was signed"
          />

          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Signature <span className="text-gray-400 font-normal">(optional — leave blank to keep existing)</span>
            </p>
            <SignatureCapture
              onSave={(data) => setSignatureData(data)}
              onClear={() => setSignatureData(null)}
              disabled={isBusy}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1"
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-primary hover:bg-primary-dark"
              disabled={isBusy}
              isLoading={isBusy}
              icon={Save}
            >
              Save & Resubmit
            </Button>
          </div>
        </form>
      )}
    </Drawer>
  );
};

export default EditMandateDrawer;
